import { useState, useCallback, useEffect, useRef } from 'react';
import { Collapse, Spin, Empty } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { AffaireService } from '../../services/AffaireService';
import { catchError } from '../../services/DaoService';
import AffaireBacklogItem from './AffaireBacklogItem';
import PropTypes from 'prop-types';

export default function AffaireDoneSection({ filters, onItemClick }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const sentinelRef = useRef(null);
  const filtersRef = useRef(filters);

  const doneFilters = useCallback(
    () => ({ ...filtersRef.current, statut: 'done' }),
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AffaireService.getInstance().search(doneFilters());
      setList(res.data.data);
      setPagination(res.data);
      setDirty(false);
    } catch (e) {
      catchError(e);
    } finally {
      setLoading(false);
    }
  }, [doneFilters]);

  // Keep filtersRef in sync
  useEffect(() => {
    filtersRef.current = filters;
  });

  // When filters change: reload if open, mark dirty if closed
  useEffect(() => {
    if (open) load();
    else setDirty(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.searchText, filters.responsable_id, filters.modele_id,
    filters.etape_id, filters.priorite_id, filters.departement_id,
    filters.date_from, filters.date_to,
  ]);

  const handleUpdate = (updated) => {
    setList(prev => prev.map(a => (a.uid === updated.uid ? updated : a)));
  };

  const handleToggle = (keys) => {
    const isOpen = keys.includes('done');
    setOpen(isOpen);
    if (isOpen && (dirty || list.length === 0)) load();
  };

  const loadMore = useCallback(async () => {
    if (!pagination?.next_page_url || loading) return;
    setLoading(true);
    try {
      const res = await AffaireService.post(pagination.next_page_url, doneFilters());
      setList(prev => [...prev, ...res.data.data]);
      setPagination(res.data);
    } catch (e) {
      catchError(e);
    } finally {
      setLoading(false);
    }
  }, [pagination, loading, doneFilters]);

  const handleIntersect = useCallback((entries) => {
    if (entries[0].isIntersecting) loadMore();
  }, [loadMore]);

  useEffect(() => {
    if (!open) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersect, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect, open]);

  const total = pagination?.total ?? null;
  const label = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, fontSize: 13 }}>
      <CheckCircleFilled style={{ color: '#52C41A' }} />
      Terminés
      {total !== null && (
        <span style={{ fontSize: 12, color: '#8C9BAB', fontWeight: 400 }}>
          ({total})
        </span>
      )}
    </span>
  );

  return (
    <Collapse
      onChange={handleToggle}
      ghost={false}
      style={{ borderTop: '1px solid var(--border-subtle, #F1F2F4)', borderRadius: 0 }}
      items={[{
        key: 'done',
        label,
        children: (
          <Spin spinning={loading && list.length === 0}>
            {list.length === 0 && !loading
              ? <div style={{ padding: '24px 0' }}><Empty description="Aucun dossier terminé" /></div>
              : (
                <div>
                  {list.map(affaire => (
                    <div key={affaire.uid} style={{ borderBottom: '1px solid var(--border-subtle, #F1F2F4)' }}>
                      <AffaireBacklogItem affaire={affaire} onItemClick={onItemClick} onUpdate={handleUpdate} />
                    </div>
                  ))}
                  <div ref={sentinelRef} style={{ height: 1 }} />
                  {loading && list.length > 0 && (
                    <div style={{ textAlign: 'center', padding: '12px 0' }}>
                      <Spin size="small" />
                    </div>
                  )}
                </div>
              )
            }
          </Spin>
        ),
      }]}
    />
  );
}

AffaireDoneSection.propTypes = {
  filters: PropTypes.object.isRequired,
  onItemClick: PropTypes.func,
};
